import { autorun, makeAutoObservable, runInAction, toJS } from 'mobx';
import { dateFormatters, getEffectiveDates } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { RootStore } from 'store/rootStore';
import { DomainProductLine, InputFilter } from 'types/domainServiceV2';
import { Cart } from 'types/cartServiceV2';
import { INITIAL_CART_OBJECT } from 'store/domain/cartStoreV2';
import { EnrollmentPeriod, EnrollmentStage } from 'types/enrollmentService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { getDomainProductLine } from 'api/operations/domainServiceV2';
import { getCoveredProductLines } from 'global/services-helpers/enrollmentService';
import { getGroupSizeRanges, isGroupSizeInRange } from 'global/utils/browsingCriteriaHelper';

interface FormFields extends DefaultFormFields {
  effectiveDate: FormControl<string>;
  zipCode: FormControl<string>;
  county: FormControl<string>;
  state: FormControl<string>;
  naics: FormControl<string>;
  sic4: FormControl<string>;
  groupSize: FormControl<string>;
}

export class BrowsingModalsStore {
  rootStore: RootStore;

  form: FormController<FormFields>;
  effectiveDates: OptionType[] = [];
  ineligibleState = false;
  ineligibleSicCode = false;
  loading = false;
  availableProductLines: DomainProductLine[] = [];
  cart: Cart;
  unavailableEffectiveDates: string[] = [];
  coveredProductLines: ProductLineId[] = [];

  async requestZipCodeInfo(): Promise<void> {
    this.setLoading(true);
    await this.rootStore.geoStore.requestZipCodeInfo(this.form.fields.zipCode.value);
    runInAction(() => {
      this.loading = false;
      const { wrongZipCodeKeyedIn, zipCodeAffiliatedData } = this.rootStore.geoStore;
      if (!wrongZipCodeKeyedIn) {
        this.form.fields.state.value = zipCodeAffiliatedData.state.id;
        this.form.fields.county.value =
          zipCodeAffiliatedData.counties.length === 1 ? zipCodeAffiliatedData.counties[0].name : '';
        this.ineligibleState = !this.rootStore.domainStoreV2.isEligibleState(zipCodeAffiliatedData.state.id);
      }
    });
  }

  async requestCreateNewCart(rateSegment?: RateSegment): Promise<void> {
    this.setLoading(true);
    const createdCart = (
      await this.rootStore.cartStoreV2.createCart(
        this.rootStore.groupStore.group.id,
        'AGENT',
        {
          effectiveDate: this.form.fields.effectiveDate.value,
          zipCode: this.form.fields.zipCode.value,
          state: this.form.fields.state.value,
          county: this.form.fields.county.value,
          naics: this.form.fields.naics.value,
          sic4: this.form.fields.sic4.value,
          groupSize: this.form.fields.groupSize.value,
          shortName: {},
        },
        '',
        rateSegment || 'GROUP',
      )
    ).data?.createCart;

    runInAction((): void => {
      if (createdCart) {
        this.cart = createdCart;
      }
      this.setLoading(false);
    });
  }

  public async getProductLines(): Promise<void> {
    this.setLoading(true);
    const inputFilter: InputFilter = {
      effectiveDate: this.form.fields.effectiveDate.value,
      zipCode: this.form.fields.zipCode.value,
      state: this.form.fields.state.value,
      county: this.form.fields.county.value,
      agencyId: this.rootStore.userStore.imAgencyId,
      sponsorId: this.rootStore.userStore.sponsorId,
      sic: this.form.fields.sic4.value || '',
    };
    const res = await getDomainProductLine(inputFilter);

    runInAction(() => {
      this.availableProductLines = res.filter(
        (domainLine) =>
          this.rootStore.domainStoreV2.isChangePlanActionAvailable(this.rootStore.userStore.userRoles) ||
          !this.coveredProductLines.includes(domainLine.productLineId),
      );
      this.setLoading(false);
    });
  }

  updateFormWithData(zipCode: string): void {
    this.form.fields.zipCode.overrideValue(zipCode);
  }

  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  setEffectiveDate(date: string): void {
    this.form.fields.effectiveDate.overrideValue(date);
  }

  overrideNAICSValue(naics: string): void {
    this.form.populateFields({ naics });
  }

  overrideSICSearchValue(sic4: string): void {
    this.form.populateFields({ sic4 });
  }

  // TODO: Remove this dynamic validation once each sponsor has its own initiate proposal flow.  // GROUP_NUMERIC_SIZE_ component is specific component for Broker Portal to allow entering Group Size value as a number (not a range)
  // Unlike NAICS, or SIC component, GROUP_NUMERIC_SIZE_ component has additional metadata:
  // Examples: GROUP_NUMERIC_SIZE_2-9, GROUP_NUMERIC_SIZE_2-9_10-49
  // So the first part of the Group Size component is a component identifier - "GROUP_NUMERIC_SIZE_"
  // The second part consist of the ranges (number dash number) combined with underscores ('_')
  // To check that the input value is in the range, we have to split
  get groupSizeValidation(): string {
    const newProposalAdditionalInputs = toJS(this.rootStore.domainStoreV2.domainHeader).properties
      ?.newProposalAdditionalInputs;

    const requiredGroupSizeRanges = getGroupSizeRanges(newProposalAdditionalInputs?.required);
    const optionalGroupSizeRanges = getGroupSizeRanges(newProposalAdditionalInputs?.optional);

    const isValid =
      requiredGroupSizeRanges.some((range: string) => isGroupSizeInRange(this.form.fields.groupSize.value, range)) || // valid in case if the field is required and value is in range
      optionalGroupSizeRanges.some((range: string) => isGroupSizeInRange(this.form.fields.groupSize.value, range)) || // valid in case if the field is optional and value is in range
      (requiredGroupSizeRanges.length === 0 &&
        optionalGroupSizeRanges.length >= 0 &&
        this.form.fields.groupSize.value.length === 0); // valid in case if the field is not required, maybe optional and empty

    return isValid
      ? ''
      : `Number of Employees should be in range of ${[...requiredGroupSizeRanges, ...optionalGroupSizeRanges].join(
          ', ',
        )} eligible. Please reach out to your Sun Life Small Business Sales Specialists directly.`;
  }

  isAdditionalInputsValid(searchElement: string, length: number, formField: string): boolean {
    const newProposalAdditionalInputs = toJS(this.rootStore.domainStoreV2.domainHeader).properties
      ?.newProposalAdditionalInputs;

    const isRequired = !!newProposalAdditionalInputs?.required.includes(searchElement);

    return isRequired ? formField.length === length : formField.length === length || formField.length === 0;
  }

  get canBrowseNext(): boolean {
    return (
      this.form.fields.zipCode.value.length === 5 &&
      this.form.fields.effectiveDate.value.length > 0 &&
      this.form.fields.state.value.length > 0 &&
      this.form.fields.county.value.length > 0 &&
      !this.ineligibleState &&
      !this.ineligibleSicCode &&
      this.isAdditionalInputsValid('NAICS', 6, this.form.fields.naics.value) &&
      this.isAdditionalInputsValid('SIC', 4, this.form.fields.sic4.value) &&
      this.groupSizeValidation.length === 0
    );
  }

  setUnavailableData(periods: EnrollmentPeriod[]): void {
    this.unavailableEffectiveDates = this.rootStore.domainStoreV2.canBrowseInstalledEffectiveDate
      ? []
      : periods.filter((period) => period.stage === EnrollmentStage.INSTALLED).map((period) => period.effectiveDate);

    this.coveredProductLines = getCoveredProductLines(periods);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.cart = INITIAL_CART_OBJECT;

    this.form = new FormController({
      effectiveDate: new FormControl<string>(''),
      zipCode: new FormControl<string>(''),
      county: new FormControl<string>('', { optional: false }),
      state: new FormControl<string>(''),
      naics: new FormControl<string>(''),
      sic4: new FormControl<string>(''),
      groupSize: new FormControl<string>(''),
    });

    autorun(() => {
      if (this.form.fields.zipCode.value.length === 5) {
        void this.requestZipCodeInfo();
      } else {
        this.form.fields.state.overrideValue('');
        this.form.fields.county.overrideValue('');
        this.ineligibleState = false;
      }
    });

    autorun(() => {
      if (this.form.fields.sic4.value.length === 4) {
        this.ineligibleSicCode = !this.rootStore.domainStoreV2.isEligibleSicCode(this.form.fields.sic4.value);
      } else {
        this.ineligibleSicCode = false;
      }
    });

    autorun(() => {
      this.effectiveDates = getEffectiveDates(
        this.rootStore.domainStoreV2.domainHeader.properties.effectiveDatesAmount || 4,
        this.rootStore.domainStoreV2.domainHeader.properties.productsAvailableYear,
        this.rootStore.domainStoreV2.domainHeader.properties.retroQuotingDaysAmount,
        this.rootStore.domainStoreV2.domainHeader.properties.productsAvailableMonth,
      )
        .filter((date) => !this.unavailableEffectiveDates.includes(date))
        .map((date) => ({
          value: date,
          label: dateFormatters.normDateToLongAmericanDate(date),
        }));
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
